import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
  TablePagination,
  Snackbar,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getCachedCategories } from '../cache/appDataCache';
import { toAutocompleteOption } from '../utils/optionResolvers';

interface RejectedRecord {
  id: string;
  websiteUrl: string;
  publisherName?: string;
  publisherEmail?: string;
  contactName?: string;
  contactEmail?: string;
  da?: number;
  dr?: number;
  traffic?: number;
  ss?: number;
  keywords?: number;
  domainAge?: string;
  imageLink?: string;
  category?: string;
  country?: string;
  language?: string;
  tat?: string;
  gbBasePrice?: number;
  liBasePrice?: number;
  comment?: string;
  rejectedBy?: string;
  rejectedByName?: string;
  rejectedAt?: string;
  uploadedBy?: string;
  createdAt: string;
}

// Fallback categories if API fails
const FALLBACK_CATEGORIES = [
  { value: 'AI_TOOL', label: 'AI Tool' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'CASINO', label: 'Casino' },
  { value: 'CBD', label: 'CBD' },
  { value: 'CRYPTO', label: 'Crypto' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'FASHION', label: 'Fashion' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'FOOD', label: 'Food' },
  { value: 'HEALTH_AND_FITNESS', label: 'Health & Fitness' },
  { value: 'HOME_IMPROVEMENT', label: 'Home Improvement' },
  { value: 'LIFESTYLE', label: 'Lifestyle' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'NEWS', label: 'News' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'TECH', label: 'Tech' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'OTHER', label: 'Other' }
];

const RejectedData: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<RejectedRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<RejectedRecord | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<RejectedRecord>>({});
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readyToSendId, setReadyToSendId] = useState<string | null>(null);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>(FALLBACK_CATEGORIES);
  const [publisherEmailFilter, setPublisherEmailFilter] = useState('');
  const [publisherEmails, setPublisherEmails] = useState<Array<{ email: string; count: number }>>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // Toast state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const showToast = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  // Get category label from value
  const getCategoryLabel = (value: string | undefined): string => {
    if (!value) return '-';
    const category = categories.find(cat => cat.value === value);
    return category ? category.label : value;
  };

  // Normalize category value to match available options
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'rejectedAt',
        sortOrder: 'desc'
      };
      if (searchText) params.search = searchText;
      if (publisherEmailFilter) params.publisherEmail = publisherEmailFilter;

      const response = await axios.get(`${API_BASE_URL}/data-rejected`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        const result = response.data.data;
        setData(result.data || []);
        setTotal(result.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error('Error fetching rejected data:', err);
      setError(err.response?.data?.message || 'Failed to fetch rejected data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText, publisherEmailFilter]);

  // Fetch categories from API on component mount (shared cache)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const list = await getCachedCategories();
        if (list.length > 0) {
          setCategories(list);
        }
      } catch (error) {
        console.error('Error fetching categories from API, using fallback:', error);
        // Categories already initialized with FALLBACK_CATEGORIES
      }
    };

    fetchCategories();
  }, []);

  // Fetch emails with multiple records (checks both publisherEmail and contactEmail) - Admin and Super Admin only
  useEffect(() => {
    const fetchPublisherEmails = async () => {
      if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/data-rejected/publisher-emails`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success && Array.isArray(response.data.data)) {
          // Backend already filters for count > 1
          setPublisherEmails(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching publisher emails:', error);
      }
    };

    fetchPublisherEmails();
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 0 when search changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Reset to page 0 when publisher email filter changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherEmailFilter]);

  const handleView = (record: RejectedRecord) => {
    setSelectedRecord(record);
    setShowViewDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this rejected record?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/data-rejected/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(data.filter(item => item.id !== id));
      setTotal(prev => prev - 1);
      showToast('Record deleted successfully', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to delete record', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      showToast('Please select records to delete', 'warning');
      return;
    }
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.size} rejected records?`)) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/data-rejected/bulk-delete`, {
        ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(data.filter(item => !selectedIds.has(item.id)));
      setTotal(prev => prev - selectedIds.size);
      setSelectedIds(new Set());
      showToast(`${selectedIds.size} records deleted successfully`, 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to delete records', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(data.map(d => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleMoveToFinal = async (id: string) => {
    if (!window.confirm('Are you sure you want to move this record back to Data Final?')) {
      return;
    }

    try {
      setMoving(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_BASE_URL}/data-rejected/${id}/move-to-final`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('Record moved to Data Final successfully', 'success');
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to move record', 'error');
    } finally {
      setMoving(false);
    }
  };

  const handleBulkMoveToFinal = async () => {
    if (selectedIds.size === 0) {
      showToast('Please select records to move', 'warning');
      return;
    }

    if (!window.confirm(`Move ${selectedIds.size} record(s) back to Data Final?`)) {
      return;
    }

    try {
      setMoving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/data-rejected/bulk-move-to-final`,
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast(response.data.message, 'success');
      setSelectedIds(new Set());
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to move records', 'error');
    } finally {
      setMoving(false);
    }
  };

  const handleEdit = (record: RejectedRecord) => {
    setEditFormData(record);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.id) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_BASE_URL}/data-rejected/${editFormData.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('Record updated successfully', 'success');
      setShowEditDialog(false);
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update record', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsReached = async () => {
    if (!editFormData.id) return;

    if (!window.confirm('Mark this record as Email Sent and move to Data Final?')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // First update the record with any changes
      await axios.put(
        `${API_BASE_URL}/data-rejected/${editFormData.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then move to DataFinal
      await axios.post(
        `${API_BASE_URL}/data-rejected/${editFormData.id}/move-to-final`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('Record marked as Email Sent and moved to Data Final successfully', 'success');
      setShowEditDialog(false);
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to mark as reached', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReadyToSend = async (row: RejectedRecord) => {
    if (readyToSendId) return;
    setReadyToSendId(row.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/data-rejected/${row.id}/move-to-in-process`,
        { status: 'READY_TO_SEND' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((prev) => prev.filter((item) => item.id !== row.id));
      setTotal((prev) => Math.max(0, prev - 1));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      showToast('Marked as Ready to send and moved to Data Management', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to mark as Ready to send', 'error');
    } finally {
      setReadyToSendId(null);
    }
  };

  const handleMarkAsEmailFailed = async () => {
    if (!editFormData.id) return;

    if (!window.confirm('Mark this record as Email Failed and move it back to Data Management?')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.put(
        `${API_BASE_URL}/data-rejected/${editFormData.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await axios.post(
        `${API_BASE_URL}/data-rejected/${editFormData.id}/move-to-in-process`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('Record marked as Email Failed and moved to Data Management', 'success');
      setShowEditDialog(false);
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to mark as Email Failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Rejected Data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Records that were marked as rejected from Data Management
          </Typography>
        </Box>
        <Chip label={`${total} records`} color="default" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Search and Actions */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, rowGap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search site URL"
              placeholder="Type to search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: 260, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')} edge="end" aria-label="clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
            {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && publisherEmails.length > 0 && (
              <Autocomplete
                size="small"
                options={[
                  { id: '', label: 'All Emails' },
                  ...publisherEmails.map((item) => ({
                    id: item.email,
                    label: `${item.email} (${item.count} records)`
                  }))
                ]}
                value={
                  !publisherEmailFilter
                    ? { id: '', label: 'All Emails' }
                    : publisherEmails
                        .map((item) => ({
                          id: item.email,
                          label: `${item.email} (${item.count} records)`
                        }))
                        .find((o) => o.id === publisherEmailFilter) || {
                        id: publisherEmailFilter,
                        label: publisherEmailFilter
                      }
                }
                onChange={(_, option) => setPublisherEmailFilter(option?.id || '')}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="Email" placeholder="Search email..." />
                )}
                sx={{ width: 280, minWidth: 280 }}
              />
            )}
            {(searchText || publisherEmailFilter) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon fontSize="small" />}
                onClick={() => {
                  setSearchText('');
                  setPublisherEmailFilter('');
                }}
                sx={{ height: 40 }}
              >
                Clear All
              </Button>
            )}
            {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && selectedIds.size > 0 && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleBulkMoveToFinal}
                  disabled={moving}
                  startIcon={moving ? <CircularProgress size={16} /> : <ArrowBackIcon />}
                  sx={{ height: 40 }}
                >
                  Move to Data Final ({selectedIds.size})
                </Button>
                {user?.role === 'SUPER_ADMIN' && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleBulkDelete}
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                    sx={{ height: 40 }}
                  >
                    Delete Selected ({selectedIds.size})
                  </Button>
                )}
              </>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">No rejected data found</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={data.length > 0 && selectedIds.size === data.length}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < data.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                    )}
                    <TableCell><strong>Site URL</strong></TableCell>
                    {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Quick Action</strong></TableCell>
                    )}
                    <TableCell align="center"><strong>Image Link</strong></TableCell>
                    <TableCell><strong>DA</strong></TableCell>
                    <TableCell><strong>DR</strong></TableCell>
                    <TableCell><strong>Traffic</strong></TableCell>
                    <TableCell><strong>Price</strong></TableCell>
                    <TableCell><strong>Comment</strong></TableCell>
                    {user?.role !== 'CONTRIBUTOR' && <TableCell><strong>Rejected By</strong></TableCell>}
                    <TableCell><strong>Rejected At</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id} hover>
                      {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(row.id)}
                            onChange={() => handleSelectRow(row.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip title={row.websiteUrl}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={row.websiteUrl.startsWith('http') ? row.websiteUrl : `https://${row.websiteUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {row.websiteUrl}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={readyToSendId !== null}
                            onClick={() => handleReadyToSend(row)}
                            startIcon={
                              readyToSendId === row.id
                                ? <CircularProgress size={14} color="inherit" />
                                : <SendIcon sx={{ fontSize: 16 }} />
                            }
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0 }}
                          >
                            Ready to send
                          </Button>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {row.imageLink ? (
                          <Tooltip title={row.imageLink}>
                            <IconButton
                              size="small"
                              component="a"
                              href={row.imageLink.startsWith('http') ? row.imageLink : `https://${row.imageLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.da || '-'}</TableCell>
                      <TableCell>{row.dr || '-'}</TableCell>
                      <TableCell>{row.traffic || '-'}</TableCell>
                      <TableCell>{row.gbBasePrice ? `$${row.gbBasePrice}` : '-'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {row.comment || '-'}
                        </Typography>
                      </TableCell>
                      {user?.role !== 'CONTRIBUTOR' && (
                        <TableCell>
                          <Typography variant="caption">
                            {row.rejectedByName || '-'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.rejectedAt ? new Date(row.rejectedAt).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(row)}>
                            <RemoveRedEyeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                          <Tooltip title="Edit, Email Sent, or Email Failed">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user?.role === 'SUPER_ADMIN' && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {data.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 500]}
            />
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Rejected Record Details</Typography>
            <Chip label="Rejected" color="error" size="small" />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Site URL</Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{selectedRecord.websiteUrl}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">DA</Typography>
                <Typography>{selectedRecord.da || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">DR</Typography>
                <Typography>{selectedRecord.dr || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Traffic</Typography>
                <Typography>{selectedRecord.traffic || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">SS</Typography>
                <Typography>{selectedRecord.ss || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Keywords</Typography>
                <Typography>{selectedRecord.keywords || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Domain Age</Typography>
                <Typography>
                  {selectedRecord.domainAge != null && String(selectedRecord.domainAge).trim() !== ''
                    ? `${String(selectedRecord.domainAge).replace(/[^0-9]/g, '')} years`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">GB Base Price</Typography>
                <Typography>{selectedRecord.gbBasePrice ? `$${selectedRecord.gbBasePrice}` : '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">LI Base Price</Typography>
                <Typography>{selectedRecord.liBasePrice ? `$${selectedRecord.liBasePrice}` : '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Typography>{getCategoryLabel(selectedRecord.category)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                <Typography>{selectedRecord.country || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Language</Typography>
                <Typography>{selectedRecord.language || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">TAT</Typography>
                <Typography>{selectedRecord.tat || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Publisher</Typography>
                <Typography>{selectedRecord.publisherName || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
                <Typography>{selectedRecord.contactName || '-'} {selectedRecord.contactEmail ? `(${selectedRecord.contactEmail})` : ''}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Rejection Comment</Typography>
                <Typography sx={{ bgcolor: 'error.lighter', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'error.light' }}>
                  {selectedRecord.comment || 'No comment provided'}
                </Typography>
              </Grid>
              {user?.role !== 'CONTRIBUTOR' && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Rejected By</Typography>
                  <Typography>{selectedRecord.rejectedByName || '-'}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Rejected At</Typography>
                <Typography>{selectedRecord.rejectedAt ? new Date(selectedRecord.rejectedAt).toLocaleString() : '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => {}} 
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Edit Record</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editFormData && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Site URL: 
                <Link 
                  href={editFormData.websiteUrl?.startsWith('http') ? editFormData.websiteUrl : `https://${editFormData.websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    fontWeight: 'bold',
                    ml: 1,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {editFormData.websiteUrl}
                </Link>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (Non-editable)
                </Typography>
              </Typography>

              <Grid container spacing={2}>
                {/* Contact fields */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={editFormData.contactName || ''}
                    onChange={(e) => setEditFormData({...editFormData, contactName: e.target.value})}
                    placeholder="Enter contact person name"
                    helperText="Name of the contact person for this site"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    type="email"
                    value={editFormData.contactEmail || ''}
                    onChange={(e) => setEditFormData({...editFormData, contactEmail: e.target.value})}
                    placeholder="contact@example.com"
                    helperText="Email of the contact person for this site"
                  />
                </Grid>

                {/* Publisher fields */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Publisher Name"
                    value={editFormData.publisherName || ''}
                    onChange={(e) => setEditFormData({...editFormData, publisherName: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Publisher Email"
                    value={editFormData.publisherEmail || ''}
                    onChange={(e) => setEditFormData({...editFormData, publisherEmail: e.target.value})}
                  />
                </Grid>

                {/* Metrics: 3 cols/row; Domain Age after TAT */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="DA"
                    type="number"
                    value={editFormData.da?.toString() || ''}
                    onChange={(e) => setEditFormData({...editFormData, da: parseInt(e.target.value) || undefined})}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="DR"
                    type="number"
                    value={editFormData.dr?.toString() || ''}
                    onChange={(e) => setEditFormData({...editFormData, dr: parseInt(e.target.value) || undefined})}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Traffic"
                    type="number"
                    value={editFormData.traffic?.toString() || ''}
                    onChange={(e) => setEditFormData({...editFormData, traffic: parseInt(e.target.value) || undefined})}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="SS"
                    type="number"
                    value={editFormData.ss?.toString() || ''}
                    onChange={(e) => setEditFormData({...editFormData, ss: parseInt(e.target.value) || undefined})}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Keywords"
                    type="number"
                    value={editFormData.keywords?.toString() || ''}
                    onChange={(e) => setEditFormData({...editFormData, keywords: parseInt(e.target.value) || undefined})}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') ? (
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="GB Base Price ($)"
                      type="number"
                      value={editFormData.gbBasePrice?.toString() || ''}
                      onChange={(e) => setEditFormData({...editFormData, gbBasePrice: parseFloat(e.target.value) || undefined})}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="e.g., 50"
                    />
                  </Grid>
                ) : (
                  <Grid item xs={4}>
                    <Autocomplete
                      options={[...categories].sort((a, b) => a.label.localeCompare(b.label))}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                      value={toAutocompleteOption(categories, editFormData.category, 'category')}
                      onChange={(_, newValue) => setEditFormData({...editFormData, category: newValue?.value || ''})}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Category" />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Language"
                    value={editFormData.language || ''}
                    onChange={(e) => setEditFormData({...editFormData, language: e.target.value})}
                  />
                </Grid>
                {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
                  <Grid item xs={4}>
                    <Autocomplete
                      options={[...categories].sort((a, b) => a.label.localeCompare(b.label))}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                      value={toAutocompleteOption(categories, editFormData.category, 'category')}
                      onChange={(_, newValue) => setEditFormData({...editFormData, category: newValue?.value || ''})}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Category" />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={editFormData.country || ''}
                    onChange={(e) => setEditFormData({...editFormData, country: e.target.value})}
                  />
                </Grid>
                {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="LI Base Price ($)"
                      type="number"
                      value={editFormData.liBasePrice?.toString() || ''}
                      onChange={(e) => setEditFormData({...editFormData, liBasePrice: parseFloat(e.target.value) || undefined})}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="e.g., 75"
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="TAT (Turnaround Time)"
                    value={editFormData.tat || ''}
                    onChange={(e) => setEditFormData({...editFormData, tat: e.target.value})}
                    placeholder="e.g., 1-2 days"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Domain Age"
                    type="number"
                    value={editFormData.domainAge || ''}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      domainAge: e.target.value.replace(/[^0-9]/g, '').slice(0, 64) || undefined
                    })}
                    placeholder="e.g., 6"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value="NO_ACTION_NEEDED"
                      label="Status"
                      disabled
                    >
                      <MenuItem value="NO_ACTION_NEEDED">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CancelIcon fontSize="small" sx={{ color: '#616161' }} /> Rejected
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Comment field */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Comment (Optional)"
                    value={editFormData.comment || ''}
                    onChange={(e) => setEditFormData({...editFormData, comment: e.target.value})}
                    placeholder="Optional: Provide a reason for rejecting this record..."
                    helperText="You can optionally add a comment to provide additional context."
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Note:</strong> Optionally add a comment.
                Use <strong>Mark as Email Sent</strong> to move to Data Final, or{' '}
                <strong>Mark as Email Failed</strong> to move back to Data Management.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => setShowEditDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            onClick={handleMarkAsEmailFailed}
            variant="contained"
            color="warning"
            disabled={saving}
            startIcon={<ErrorOutlineIcon />}
            sx={{ color: 'white' }}
          >
            {saving ? 'Processing...' : 'Mark as Email Failed'}
          </Button>
          <Button 
            onClick={handleMarkAsReached} 
            variant="contained" 
            color="success"
            disabled={saving}
            startIcon={<CheckCircleIcon />}
            sx={{ color: 'white' }}
          >
            {saving ? 'Processing...' : 'Mark as Email Sent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ color: 'white' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RejectedData;
